"use strict";

// Export the main async function
export const initGsap = async () => {

    let gsap, ScrollTrigger; // Declare variables for wider scope within initGsap

    try {
        // Load Core and ScrollTrigger first as they are widely used
        const [gsapModule, scrollTriggerModule] = await Promise.all([
            import('gsap'),
            import('gsap/ScrollTrigger')
        ]);

        gsap = gsapModule.default;
        ScrollTrigger = scrollTriggerModule.ScrollTrigger; // Assuming named export

        // Register ScrollTrigger immediately
        gsap.registerPlugin(ScrollTrigger);

    } catch (error) {
        console.error("Failed to load GSAP Core or ScrollTrigger:", error);
        return; // Can't proceed without these
    }

    // --- Call functions NOT needing Flip ---
    // Pass gsap/ScrollTrigger instance to them
    initLines(gsap, ScrollTrigger);
    initHomeBrand(gsap, ScrollTrigger);
    initStickyFilters(gsap, ScrollTrigger, '.nav--agenda-filters');

    // --- Call the function that conditionally loads and uses Flip ---
    // Pass gsap so Flip can be registered inside
    initSlidingBoxHover(gsap);
};

function initLines(gsap, ScrollTrigger) {
    const lines = gsap.utils.toArray('[data-gsap-line]');

    if (!lines.length) {
        console.warn("GSAP initLines: No elements found with [data-gsap-line]");
        return;
    }

    ScrollTrigger.batch(lines, {
        start: "top bottom",
        once: true,
        onEnter: batch => {
            gsap.to(batch, {
                scaleX: 1,
                duration: 1,
                delay: 0.5,
            });
        },
    });
}

async function initSlidingBoxHover(gsap) {
    const listContainer = document.querySelector('.hero__thumbs');
    if (!listContainer) return;
    const slidingBox = document.querySelector('.hero__thumbs-box');
    if (!slidingBox) return;

    let Flip;
    try {
        const flipModule = await import('gsap/Flip'); // Dynamically import Flip
        Flip = flipModule.Flip; // Assuming named export
        // Register Flip using the passed gsap instance
        gsap.registerPlugin(Flip);

    } catch (error) {
        console.error("Failed to load GSAP Flip plugin:", error);
        return; // Cannot run this animation without Flip
    }

    const listItems = gsap.utils.toArray('.hero__thumbs .swiper-slide');
    let hideTimeout = null;
    const HIDE_DELAY = 150;

    function initializeBox() {
        if (listItems.length > 0) {
            const firstThumb = listItems[0].querySelector('.hero__thumb');
            if(firstThumb){
                gsap.set(slidingBox, {
                    top: listItems[0].offsetTop,
                    left: listItems[0].offsetLeft + firstThumb.offsetLeft,
                    width: firstThumb.offsetWidth,
                    height: firstThumb.offsetHeight,
                    opacity: 0
                });
            }
        }
    }
    gsap.delayedCall(0.01, initializeBox);

    listItems.forEach(item => {
        const thumb = item.querySelector('.hero__thumb');
        if(!thumb) return; // Add guard for missing thumb
        item.addEventListener('mouseenter', () => {
            if (hideTimeout) { clearTimeout(hideTimeout); hideTimeout = null; }
            const state = Flip.getState(slidingBox);
            gsap.set(slidingBox, {
                top: item.offsetTop,
                left: item.offsetLeft + thumb.offsetLeft,
                width: thumb.offsetWidth,
                height: thumb.offsetHeight,
                opacity: 1,
            });
            Flip.from(state, {
                duration: 0.3,
                ease: "power2.out",
                absolute: true // Often needed for layout changes
            });
        });
    });

    listContainer.addEventListener('mouseleave', () => {
        if (hideTimeout) { clearTimeout(hideTimeout); }
        hideTimeout = setTimeout(() => {
            gsap.to(slidingBox, {
                opacity: 0,
                duration: .15,
                ease: "power1.inOut",
                overwrite: true
            });
            hideTimeout = null;
        }, HIDE_DELAY);
    });
}

function initHomeBrand(gsap, ScrollTrigger){
    const introSection = document.querySelector('.brand-intro');
    if (!introSection) return; // Exit if the section is not found
    const imgWrapper = document.querySelector('.brand-intro__img');
    if (!imgWrapper) return; // Exit if the image element is not found
    const imgs = imgWrapper.querySelectorAll('img');
    if (!imgs[0] || !imgs[1]) return; // Exit if the images are not found

    // Use matchMedia for responsive animations
    let mm = gsap.matchMedia();

    // --- Desktop Animation (screens >= 992px) ---
    mm.add("(min-width: 992px)", () => {
        const linesContainer = document.querySelector('.lines-container');

        // Ensure elements exist
        if (!linesContainer) {
            console.error("Required elements not found for desktop animation.");
            return;
        }

        const emptyDurationPercent = linesContainer.offsetHeight / introSection.offsetHeight; // Calculate the percentage of empty space

        // Create a timeline for the mask animation (starts before pin)
        gsap.timeline({
            scrollTrigger: {
                trigger: introSection,
                start: "top 50%",      // Animation starts earlier
                end: "top top",        // Animation ends when section reaches top
                scrub: 1,
                invalidateOnRefresh: true
            }
        })
        .to(imgs[0], {
            maskSize: "200%",
            duration: 1
        }, 0)
        .to(imgs[1], {
            opacity: 1,
            duration: 1
        }, 0);

        // Create a timeline for the pinning (starts at top)
        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: introSection,
                start: "top top",      // Pin starts when section reaches top
                end: () => "+=" + (introSection.offsetHeight - linesContainer.offsetHeight >= window.innerHeight ? linesContainer.offsetHeight : 0),
                pin: imgWrapper,
                pinSpacing: false,
                scrub: 1,
                //markers: true,
                invalidateOnRefresh: true
            }
        });

        tl.to(linesContainer, {
            duration: emptyDurationPercent * 2
        })


        return () => {
            // Optional: Clean up function for when the media query no longer matches
        };
    });

    // --- Mobile Animation Placeholder (screens < 992px) ---
    mm.add("(max-width: 991px)", () => {

        // Create scrolltrigger just to play when the section is in view and play reverse on leaveBack

        ScrollTrigger.create({
            trigger: imgWrapper,
            start: "center center", // When the top of the trigger hits the center of the viewport
            scrub: 1,
            // markers: true,         // Uncomment for debugging
            invalidateOnRefresh: true, // Recalculate on resize
            onEnter: () => {
                gsap.to(imgs[0], {
                    maskSize: "200%",
                    duration: 1
                });
                gsap.to(imgs[1], {
                    opacity: 1,
                    duration: 1
                });
            },
            onLeaveBack: () => {
                gsap.to(imgs[0], {
                    maskSize: "50%",
                    duration: 1
                });
                gsap.to(imgs[1], {
                    opacity: 0,
                    duration: 1
                });
            }
        });

        return () => {
            // Optional: Clean up function for mobile
        };
    });
    
}

function initStickyFilters(gsap, ScrollTrigger, classSelector){
    // Filters are "static" but neet to be fixed to bottom when element is out of view
    const filters = document.querySelector(classSelector);
    if (!filters) return; // Exit if the filters element is not found

    ScrollTrigger.create({
        trigger: filters,
        start: "bottom top",
        //markers: true,
        invalidateOnRefresh: true,
        onEnter: () => {
            filters.classList.add(`${classSelector.replace('.','')}--fixed`);
            gsap.fromTo(filters, {
                autoAlpha: 0
            }, {
                duration: .3,
                autoAlpha: 1,
                ease: "power2.inOut"
            });
        },
        onLeaveBack: () => {
            filters.classList.remove(`${classSelector.replace('.','')}--fixed`);
        }
    });

    const footer = document.querySelector('.footer');
    if (!footer) return;
    ScrollTrigger.create({
        trigger: footer,
        start: "top bottom",
        //markers: true,
        invalidateOnRefresh: true,
        onEnter: () => {
            gsap.to(filters, {
                duration: .3,
                autoAlpha: 0,
                ease: "power2.inOut"
            });
        },
        onLeaveBack: () => {
            gsap.to(filters, {
                duration: .3,
                autoAlpha: 1,
                ease: "power2.inOut"
            });
        }
    });
}